import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, ChevronDown } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { PHONE, PHONE_HREF, EMAIL, EMAIL_HREF, SERVICE_OPTIONS } from "../lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", service: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in your name, email, phone, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: form.service || "General Inquiry",
        message: form.message,
      });
      toast.success("Request received. Our team will reach out to you shortly.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please call us at " + PHONE);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-zinc-800 rounded-none px-0 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500 transition-colors";

  return (
    <section id="contact" className="relative bg-[#221b14] py-24 sm:py-36 scroll-mt-20">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(212,175,55,0.08),transparent_45%)]" />
      <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline>Free 15-Minute Consultation</Overline>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-6 font-serif font-bold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
                Let's Build Your <span className="text-gold-foil">Legacy</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-zinc-400 font-light leading-relaxed">
                Tell us about your goals, challenges, and vision. Your first
                consultation is completely free and confidential.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 space-y-5">
                <a
                  href={PHONE_HREF}
                  data-testid="contact-phone-link"
                  className="flex items-center gap-4 group"
                >
                  <span className="flex h-12 w-12 items-center justify-center border border-amber-500/30 text-amber-400 group-hover:bg-amber-500 group-hover:text-black transition-colors">
                    <Phone className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-xs uppercase tracking-widest text-zinc-500">
                      Call Us
                    </span>
                    <span className="text-lg text-white">{PHONE}</span>
                  </span>
                </a>
                <a
                  href={EMAIL_HREF}
                  data-testid="contact-email-link"
                  className="flex items-center gap-4 group"
                >
                  <span className="flex h-12 w-12 items-center justify-center border border-amber-500/30 text-amber-400 group-hover:bg-amber-500 group-hover:text-black transition-colors">
                    <Mail className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-xs uppercase tracking-widest text-zinc-500">
                      Email Us
                    </span>
                    <span className="text-base sm:text-lg text-white break-all">{EMAIL}</span>
                  </span>
                </a>
                <div className="flex items-center gap-4">
                  <span className="flex h-12 w-12 items-center justify-center border border-amber-500/30 text-amber-400">
                    <MapPin className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-xs uppercase tracking-widest text-zinc-500">
                      Service Area
                    </span>
                    <span className="text-lg text-white">Nationwide · United States</span>
                  </span>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="bg-zinc-950/60 border border-zinc-800/80 p-8 sm:p-10"
              >
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-zinc-500">
                      Full Name
                    </label>
                    <input
                      data-testid="contact-name-input"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="John Smith"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-zinc-500">
                      Email
                    </label>
                    <input
                      data-testid="contact-email-input"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@email.com"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-zinc-500">
                      Phone
                    </label>
                    <input
                      data-testid="contact-phone-input"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="(000) 000-0000"
                      className={inputCls}
                    />
                  </div>
                  <div className="relative">
                    <label className="text-xs uppercase tracking-widest text-zinc-500">
                      Service Interested In
                    </label>
                    <select
                      data-testid="contact-service-select"
                      value={form.service}
                      onChange={update("service")}
                      className={`${inputCls} appearance-none cursor-pointer text-white`}
                    >
                      <option value="" className="bg-zinc-900">
                        Select a service
                      </option>
                      {SERVICE_OPTIONS.map((o) => (
                        <option key={o} value={o} className="bg-zinc-900">
                          {o}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-0 bottom-3.5 h-4 w-4 text-amber-500" />
                  </div>
                </div>

                <div className="mt-6">
                  <label className="text-xs uppercase tracking-widest text-zinc-500">
                    How Can We Help?
                  </label>
                  <textarea
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={update("message")}
                    rows={4}
                    placeholder="Tell us about your goals..."
                    className={`${inputCls} resize-none`}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                  className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-gold-foil text-black text-[13px] font-bold tracking-[0.14em] uppercase px-10 py-4 hover:brightness-110 transition-all duration-300 disabled:opacity-60"
                >
                  <Send className="h-4 w-4" />
                  {loading ? "Sending..." : "Book My Free 15-Minute Consultation"}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
